import { layers, namedFlavor } from "@protomaps/basemaps";

export const BASEMAP_SOURCE_ID = "protomaps";
export const PLACES_SOURCE_ID = "hotple-places";
export const PLACE_LAYER_ID = "hotple-place-points";
export const PLACE_LABEL_LAYER_ID = "hotple-place-labels";
// 같은 자리에 쌓인 곳의 개수를 얹는 층(예: 신세계 본점 한 점에 534곳).
export const PLACE_STACK_COUNT_LAYER_ID = "hotple-place-stack-count";
export const CLUSTER_LAYER_ID = "hotple-place-clusters";
export const CLUSTER_COUNT_LAYER_ID = "hotple-place-cluster-count";

const HOTPLE_PLACE_SHADOW_LAYER_ID = "hotple-place-shadow";
const HOTPLE_PLACE_HALO_LAYER_ID = "hotple-place-halo";
const HOTPLE_SELECTED_PLACE_LAYER_ID = "hotple-selected-place-ring";
const HOTPLE_CLUSTER_SHADOW_LAYER_ID = "hotple-place-cluster-shadow";

export const HOTPLE_KOREA_COLORS = {
  background: "#F7F8F5",
  land: "#F6F7F2",
  residential: "#F4F5F0",
  commercial: "#F7F3EA",
  industrial: "#F0F2F0",
  water: "#CFE8F7",
  waterOutline: "#B9DDEB",
  park: "#DDEEDB",
  forest: "#D2E7CE",
  grass: "#E6F3E2",
  building: "#E8E6DF",
  buildingOutline: "#D8D6CE",
  minorRoad: "#FFFFFF",
  minorRoadCasing: "#E2E4E0",
  mediumRoad: "#FBFBF8",
  mediumRoadCasing: "#D0D4D1",
  primaryRoad: "#F3E3B5",
  primaryRoadCasing: "#E3C979",
  highway: "#F5D88A",
  highwayCasing: "#D8B85F",
  rail: "#C8CCD2",
  boundary: "#D5D9DC",
  labelMain: "#2F363D",
  labelNormal: "#59636D",
  labelMuted: "#87909A",
  labelHalo: "#FFFFFF",
  waterLabel: "#5F8EA6",
  parkLabel: "#4E7D58",
  roadLabel: "#7A6A3A",
  hotpleMain: "#e5136f",
  hotpleSelected: "#d90785",
  hotpleCafe: "#e5136f",
  hotpleFood: "#e5136f",
  hotplePlay: "#e5136f",
  hotpleView: "#e5136f",
  clusterSoft: "#FCE4EF",
  clusterTextMedium: "#c10d60",
};

export const MAP_COLORS = HOTPLE_KOREA_COLORS;

const LIMITED_POI_KINDS = [
  "aerodrome",
  "attraction",
  "building",
  "ferry_terminal",
  "forest",
  "garden",
  "hospital",
  "library",
  "marina",
  "museum",
  "park",
  "post_office",
  "school",
  "stadium",
  "station",
  "theatre",
  "townhall",
  "university",
  "zoo",
];

const CATEGORY_MATCHERS = [
  {
    color: HOTPLE_KOREA_COLORS.hotpleCafe,
    tokens: [
      "cafe",
      "coffee",
      "dessert",
      "bakery",
      "\uCE74\uD398",
      "\uCEE4\uD53C",
      "\uB514\uC800\uD2B8",
      "\uBCA0\uC774\uCEE4\uB9AC",
    ],
  },
  {
    color: HOTPLE_KOREA_COLORS.hotplePlay,
    tokens: [
      "activity",
      "play",
      "experience",
      "leisure",
      "\uB180",
      "\uCCB4\uD5D8",
      "\uC561\uD2F0\uBE44\uD2F0",
      "\uBB38\uD654",
      "\uACF5\uC5F0",
      "\uC804\uC2DC",
    ],
  },
  {
    color: HOTPLE_KOREA_COLORS.hotpleView,
    tokens: [
      "view",
      "walk",
      "park",
      "nature",
      "tour",
      "\uAD00\uAD11",
      "\uBA85\uC18C",
      "\uACF5\uC6D0",
      "\uC0B0\uCC45",
      "\uC790\uC5F0",
      "\uC804\uB9DD",
      "\uD574\uBCC0",
    ],
  },
  {
    color: HOTPLE_KOREA_COLORS.hotpleFood,
    tokens: [
      "food",
      "restaurant",
      "dining",
      "bar",
      "pub",
      "\uC74C\uC2DD",
      "\uC2DD\uB2F9",
      "\uB9DB\uC9D1",
      "\uD55C\uC2DD",
      "\uC911\uC2DD",
      "\uC77C\uC2DD",
      "\uC591\uC2DD",
      "\uC220\uC9D1",
      "\uC694\uB9AC",
    ],
  },
];

// 지도 글꼴·아이콘은 우리가 낸다(`public/map/`). 주소는 **절대 주소**여야 한다 —
// MapLibre 는 `sprite` 가 상대 주소면 스타일을 통째로 거부한다
// ("Invalid sprite URL … must be absolute"). 2026-08-09 배포에서 실제로 겪었다.
// 지도는 브라우저에서만 그려지므로 window 를 봐도 되지만, 서버에서 스타일을 만들 때를
// 대비해 값이 없으면 그대로 둔다(그때는 그려지지 않으니 문제가 되지 않는다).
const mapAssetUrl = (path) =>
  typeof window !== "undefined" && window.location
    ? `${window.location.origin}${path}`
    : path;

const normalizePmtilesUrl = (url) => {
  const value = typeof url === "string" ? url.trim() : "";
  return value.startsWith("pmtiles://") ? value : `pmtiles://${value}`;
};

const withPaint = (layer, paint) => ({
  ...layer,
  paint: {
    ...(layer.paint || {}),
    ...paint,
  },
});

const withLayout = (layer, layout) => ({
  ...layer,
  layout: {
    ...(layer.layout || {}),
    ...layout,
  },
});

const withTextPaint = (layer, color, opacity = 1) =>
  withPaint(layer, {
    "text-color": color,
    "text-opacity": opacity,
    "text-halo-color": HOTPLE_KOREA_COLORS.labelHalo,
    "text-halo-width": 1.35,
    "text-halo-blur": 0.25,
  });

const withNotoText = (layer, weight = "Regular") =>
  withLayout(layer, {
    "text-font": [`Noto Sans ${weight}`],
  });

const getRoadColor = (id, isCasing = false) => {
  if (id.includes("highway")) {
    return isCasing
      ? HOTPLE_KOREA_COLORS.highwayCasing
      : HOTPLE_KOREA_COLORS.highway;
  }
  if (id.includes("major")) {
    return isCasing
      ? HOTPLE_KOREA_COLORS.primaryRoadCasing
      : HOTPLE_KOREA_COLORS.primaryRoad;
  }
  if (id.includes("link")) {
    return isCasing
      ? HOTPLE_KOREA_COLORS.mediumRoadCasing
      : HOTPLE_KOREA_COLORS.mediumRoad;
  }
  return isCasing
    ? HOTPLE_KOREA_COLORS.minorRoadCasing
    : HOTPLE_KOREA_COLORS.minorRoad;
};

const isRoadLayer = (id) => id.startsWith("roads_");

const isRoadCasingLayer = (id) => id.includes("casing");

const isRoadFillLayer = (id) =>
  isRoadLayer(id) &&
  !isRoadCasingLayer(id) &&
  !id.includes("label") &&
  !id.includes("shield") &&
  !id.includes("oneway") &&
  id !== "roads_rail";

const styleLanduseLayer = (layer) => {
  switch (layer.id) {
    case "background":
      return withPaint(layer, {
        "background-color": HOTPLE_KOREA_COLORS.background,
      });
    case "earth":
      return withPaint(layer, {
        "fill-color": HOTPLE_KOREA_COLORS.land,
        "fill-opacity": 1,
      });
    case "landcover":
      return withPaint(layer, {
        "fill-color": [
          "match",
          ["get", "kind"],
          ["forest", "wood"],
          HOTPLE_KOREA_COLORS.forest,
          ["grass", "grassland", "scrub", "meadow"],
          HOTPLE_KOREA_COLORS.grass,
          HOTPLE_KOREA_COLORS.park,
        ],
        "fill-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          5,
          0.28,
          12,
          0.45,
          16,
          0.58,
        ],
      });
    case "landuse_park":
      return withPaint(layer, {
        "fill-color": [
          "match",
          ["get", "kind"],
          ["forest", "wood", "nature_reserve", "protected_area"],
          HOTPLE_KOREA_COLORS.forest,
          ["grass", "grassland", "golf_course", "park", "cemetery"],
          HOTPLE_KOREA_COLORS.park,
          HOTPLE_KOREA_COLORS.grass,
        ],
        "fill-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          5,
          0.36,
          12,
          0.52,
          16,
          0.66,
        ],
      });
    case "landuse_urban_green":
      return withPaint(layer, {
        "fill-color": HOTPLE_KOREA_COLORS.grass,
        "fill-opacity": 0.48,
      });
    case "landuse_hospital":
    case "landuse_school":
      return withPaint(layer, {
        "fill-color": HOTPLE_KOREA_COLORS.commercial,
        "fill-opacity": 0.38,
      });
    case "landuse_industrial":
      return withPaint(layer, {
        "fill-color": HOTPLE_KOREA_COLORS.industrial,
        "fill-opacity": 0.42,
      });
    case "landuse_pedestrian":
      return withPaint(layer, {
        "fill-color": HOTPLE_KOREA_COLORS.commercial,
        "fill-opacity": 0.22,
      });
    case "landuse_beach":
      return withPaint(layer, {
        "fill-color": "#F4E7C7",
        "fill-opacity": 0.44,
      });
    default:
      return layer;
  }
};

const styleWaterLayer = (layer) => {
  if (layer.id === "water") {
    return withPaint(layer, {
      "fill-color": HOTPLE_KOREA_COLORS.water,
      "fill-outline-color": HOTPLE_KOREA_COLORS.waterOutline,
      "fill-opacity": 0.95,
    });
  }
  if (layer.id === "water_stream" || layer.id === "water_river") {
    return withPaint(layer, {
      "line-color": HOTPLE_KOREA_COLORS.water,
      "line-opacity": 0.92,
    });
  }
  return layer;
};

const styleBuildingLayer = (layer) => {
  if (layer.id !== "buildings") return layer;

  return {
    ...withPaint(layer, {
      "fill-color": HOTPLE_KOREA_COLORS.building,
      "fill-outline-color": HOTPLE_KOREA_COLORS.buildingOutline,
      "fill-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        13.5,
        0,
        15,
        0.32,
        16,
        0.44,
        18,
        0.58,
      ],
    }),
    minzoom: 14,
  };
};

const styleRoadLayer = (layer) => {
  const { id } = layer;

  if (id === "roads_rail") {
    return withPaint(layer, {
      "line-color": HOTPLE_KOREA_COLORS.rail,
      "line-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        8,
        0.28,
        12,
        0.42,
        16,
        0.62,
      ],
    });
  }

  if (isRoadCasingLayer(id)) {
    return withPaint(layer, {
      "line-color": getRoadColor(id, true),
      "line-opacity": id.includes("tunnel") ? 0.35 : 0.72,
    });
  }

  if (isRoadFillLayer(id)) {
    return withPaint(layer, {
      "line-color": getRoadColor(id, false),
      "line-opacity": id.includes("tunnel") ? 0.58 : 0.96,
    });
  }

  if (id === "roads_oneway") {
    return withPaint(layer, {
      "icon-opacity": 0.32,
      "icon-color": HOTPLE_KOREA_COLORS.labelMuted,
    });
  }

  if (id === "roads_shields") {
    return withTextPaint(
      withNotoText(layer, "Medium"),
      HOTPLE_KOREA_COLORS.roadLabel,
      0.78
    );
  }

  if (id === "roads_labels_major") {
    return withTextPaint(
      withNotoText(layer, "Medium"),
      HOTPLE_KOREA_COLORS.roadLabel,
      0.88
    );
  }

  if (id === "roads_labels_minor") {
    return withTextPaint(
      withNotoText(layer),
      "#747C83",
      ["interpolate", ["linear"], ["zoom"], 15, 0.48, 17, 0.78]
    );
  }

  return layer;
};

const styleBoundaryLayer = (layer) => {
  if (layer.id !== "boundaries" && layer.id !== "boundaries_country") {
    return layer;
  }

  return withPaint(layer, {
    "line-color": HOTPLE_KOREA_COLORS.boundary,
    "line-opacity": layer.id === "boundaries_country" ? 0.5 : 0.32,
    "line-width": layer.id === "boundaries_country" ? 0.9 : 0.55,
  });
};

const styleLabelLayer = (layer) => {
  switch (layer.id) {
    case "water_waterway_label":
    case "water_label_ocean":
    case "water_label_lakes":
      return withTextPaint(
        withNotoText(layer),
        HOTPLE_KOREA_COLORS.waterLabel,
        0.82
      );
    case "earth_label_islands":
      return withTextPaint(
        withNotoText(layer),
        HOTPLE_KOREA_COLORS.labelMuted,
        0.75
      );
    case "places_country":
    case "places_region":
      return withTextPaint(
        withNotoText(layer, "Medium"),
        HOTPLE_KOREA_COLORS.labelMain,
        0.82
      );
    case "places_locality":
      return withTextPaint(
        withNotoText(layer, "Medium"),
        HOTPLE_KOREA_COLORS.labelMain,
        0.9
      );
    case "places_subplace":
      return withTextPaint(
        withNotoText(layer),
        HOTPLE_KOREA_COLORS.labelNormal,
        0.72
      );
    case "address_label":
      return withTextPaint(
        withNotoText(layer),
        HOTPLE_KOREA_COLORS.labelMuted,
        0.28
      );
    default:
      return layer;
  }
};

const stylePoiLayer = (layer) => {
  if (layer.id !== "pois") return layer;

  return {
    ...withLayout(layer, {
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        13,
        10,
        16,
        11.5,
        18,
        12.5,
      ],
      "icon-image": [
        "match",
        ["get", "kind"],
        "station",
        "train_station",
        "townhall",
        "building",
        ["get", "kind"],
      ],
      "icon-size": ["interpolate", ["linear"], ["zoom"], 13, 0.75, 17, 0.9],
    }),
    minzoom: 13,
    filter: [
      "all",
      ["in", ["get", "kind"], ["literal", LIMITED_POI_KINDS]],
      [">=", ["zoom"], ["+", ["get", "min_zoom"], 0.8]],
    ],
    paint: {
      ...(layer.paint || {}),
      "text-color": [
        "match",
        ["get", "kind"],
        ["park", "forest", "garden", "zoo"],
        HOTPLE_KOREA_COLORS.parkLabel,
        [
          "station",
          "townhall",
          "hospital",
          "university",
          "school",
          "museum",
          "attraction",
        ],
        HOTPLE_KOREA_COLORS.labelNormal,
        HOTPLE_KOREA_COLORS.labelMuted,
      ],
      "text-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        12,
        0,
        14,
        0.45,
        17,
        0.72,
      ],
      "icon-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        12,
        0,
        14,
        0.32,
        17,
        0.55,
      ],
      "text-halo-color": HOTPLE_KOREA_COLORS.labelHalo,
      "text-halo-width": 1.25,
      "text-halo-blur": 0.3,
    },
  };
};

const styleBaseLayer = (layer) =>
  [
    styleLanduseLayer,
    styleWaterLayer,
    styleBuildingLayer,
    styleRoadLayer,
    styleBoundaryLayer,
    styleLabelLayer,
    stylePoiLayer,
  ].reduce((currentLayer, styleLayer) => styleLayer(currentLayer), layer);

const createSoftLanduseLayers = () => [
  {
    id: "hotple-landuse-residential",
    type: "fill",
    source: BASEMAP_SOURCE_ID,
    "source-layer": "landuse",
    filter: ["==", "kind", "residential"],
    paint: {
      "fill-color": HOTPLE_KOREA_COLORS.residential,
      "fill-opacity": 0.34,
    },
  },
  {
    id: "hotple-landuse-commercial",
    type: "fill",
    source: BASEMAP_SOURCE_ID,
    "source-layer": "landuse",
    filter: ["==", "kind", "commercial"],
    paint: {
      "fill-color": HOTPLE_KOREA_COLORS.commercial,
      "fill-opacity": 0.3,
    },
  },
];

export const buildHotpleKoreaBasemapLayers = () => {
  const baseLayers = layers(BASEMAP_SOURCE_ID, namedFlavor("light"), {
    lang: "ko",
  }).map(styleBaseLayer);
  const landcoverIndex = baseLayers.findIndex(
    (layer) => layer.id === "landcover"
  );

  if (landcoverIndex < 0) return baseLayers;

  return [
    ...baseLayers.slice(0, landcoverIndex + 1),
    ...createSoftLanduseLayers(),
    ...baseLayers.slice(landcoverIndex + 1),
  ];
};

export const getHotpleMarkerColor = (
  categoryText,
  fallback = HOTPLE_KOREA_COLORS.hotpleMain
) => {
  const normalizedText = String(categoryText || "").toLowerCase();
  const matcher = CATEGORY_MATCHERS.find(({ tokens }) =>
    tokens.some((token) => normalizedText.includes(token.toLowerCase()))
  );

  return matcher?.color || fallback;
};

export const buildHotpleKoreaStyle = ({
  pmtilesUrl,
  markersGeoJson,
  markerColor = HOTPLE_KOREA_COLORS.hotpleMain,
  selectedMarkerColor = HOTPLE_KOREA_COLORS.hotpleSelected,
  clusterColor = HOTPLE_KOREA_COLORS.hotpleMain,
  clusterMarkers = true,
}) => ({
  version: 8,
  // 글꼴 조각과 아이콘은 우리 것을 쓴다(`public/map/`). 예전에는 protomaps.github.io
  // 에서 받아왔는데 남의 집이라, 그쪽이 주소를 바꾸거나 내리면 지도 글씨가 통째로
  // 사라진다. 캐시도 10분짜리였다. 받아 두는 방법은 `docs/map-assets.md` 참고.
  // 한글은 내려받지 않는다 — MapLibre 가 기기 글꼴로 직접 그린다(localIdeographFontFamily 기본값).
  glyphs: mapAssetUrl("/map/fonts/{fontstack}/{range}.pbf"),
  sprite: mapAssetUrl("/map/sprites/light"),
  sources: {
    [BASEMAP_SOURCE_ID]: {
      type: "vector",
      url: normalizePmtilesUrl(pmtilesUrl),
      attribution:
        '<a href="https://protomaps.com">Protomaps</a>, <a href="https://openstreetmap.org">OpenStreetMap</a>',
    },
    [PLACES_SOURCE_ID]: {
      type: "geojson",
      data: markersGeoJson,
      ...(clusterMarkers
        ? {
            cluster: true,
            // 확대해도 계속 묶는다. 화면상 48px 기준이라 확대하면 저절로 풀리고,
            // 한 건물에 수백 곳이 겹쳐 동그란 뭉치가 되는 일이 없어진다.
            // (예전 16 은 그 지점부터 낱개를 그려 뭉치가 생겼다 — 2026-08-15)
            clusterMaxZoom: 21,
            clusterRadius: 48,
          }
        : {}),
    },
  },
  layers: [
    ...buildHotpleKoreaBasemapLayers(),
    {
      id: HOTPLE_CLUSTER_SHADOW_LAYER_ID,
      type: "circle",
      source: PLACES_SOURCE_ID,
      filter: ["has", "point_count"],
      paint: {
        "circle-color": "rgba(20, 12, 16, 0.16)",
        "circle-radius": ["step", ["get", "point_count"], 20, 10, 24, 50, 30],
        "circle-blur": 0.85,
        "circle-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          5,
          0.1,
          14,
          0.16,
          18,
          0.12,
        ],
        "circle-translate": [0, 2],
      },
    },
    {
      id: CLUSTER_LAYER_ID,
      type: "circle",
      source: PLACES_SOURCE_ID,
      filter: ["has", "point_count"],
      paint: {
        // Calm white "chips" with a faint pink ring — the zoomed-out view reads
        // as soft neutral bubbles, not a wall of bold pink rings. Denser
        // clusters get a slightly stronger tint so size+tint still signal
        // density. Brand pink is reserved for the actual place pins.
        "circle-color": [
          "interpolate",
          ["linear"],
          ["get", "point_count"],
          2,
          "#ffffff",
          30,
          "#f1eff2",
          200,
          "#ddd8e0",
        ],
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["get", "point_count"],
          2,
          17,
          10,
          20,
          50,
          25,
          300,
          31,
        ],
        "circle-stroke-color": [
          "interpolate",
          ["linear"],
          ["get", "point_count"],
          2,
          "rgba(38,34,44,0.28)",
          50,
          "rgba(38,34,44,0.45)",
          300,
          "rgba(38,34,44,0.62)",
        ],
        "circle-stroke-width": 1.3,
      },
    },
    {
      id: CLUSTER_COUNT_LAYER_ID,
      type: "symbol",
      source: PLACES_SOURCE_ID,
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["concat", ["get", "point_count_abbreviated"], "곳"],
        "text-font": ["Noto Sans Medium"],
        "text-size": ["step", ["get", "point_count"], 11, 10, 11.5, 50, 12],
        "text-allow-overlap": true,
      },
      paint: {
        "text-color": "#2b2630",
      },
    },
    {
      id: HOTPLE_PLACE_SHADOW_LAYER_ID,
      type: "circle",
      source: PLACES_SOURCE_ID,
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": "rgba(0, 0, 0, 0.18)",
        "circle-radius": [
          "case",
          ["boolean", ["get", "selected"], false],
          14,
          ["boolean", ["get", "isSearchResultPin"], false],
          14,
          [
            "interpolate",
            ["linear"],
            ["to-number", ["coalesce", ["get", "numberOfVisitorReviews"], 0]],
            0,
            3.8,
            300,
            6,
            1200,
            8,
            6000,
            10,
          ],
        ],
        "circle-blur": 0.7,
        "circle-opacity": 0.22,
        "circle-translate": [0, 2],
      },
    },
    {
      id: HOTPLE_PLACE_HALO_LAYER_ID,
      type: "circle",
      source: PLACES_SOURCE_ID,
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": ["coalesce", ["get", "color"], markerColor],
        "circle-radius": [
          "case",
          ["boolean", ["get", "selected"], false],
          14,
          ["boolean", ["get", "isSearchResultPin"], false],
          14,
          [
            "interpolate",
            ["linear"],
            ["to-number", ["coalesce", ["get", "numberOfVisitorReviews"], 0]],
            0,
            0,
            800,
            8,
            6000,
            13,
          ],
        ],
        "circle-opacity": [
          "case",
          ["boolean", ["get", "selected"], false],
          0.24,
          ["boolean", ["get", "isSearchResultPin"], false],
          0.24,
          0.14,
        ],
      },
    },
    {
      id: PLACE_LAYER_ID,
      type: "circle",
      source: PLACES_SOURCE_ID,
      filter: ["!", ["has", "point_count"]],
      paint: {
        // Popularity-graded intensity: quiet light dots by default, deep brand
        // pink only for busy places (naver review count) — the eye is drawn to
        // the genuinely notable spots instead of a wall of uniform pink.
        "circle-color": [
          "case",
          ["boolean", ["get", "selected"], false],
          selectedMarkerColor,
          ["boolean", ["get", "isSearchResultPin"], false],
          selectedMarkerColor,
          [
            "coalesce",
            ["get", "color"],
            [
              "interpolate",
              ["linear"],
              ["to-number", ["coalesce", ["get", "numberOfVisitorReviews"], 0]],
              0,
              "#f7cfdf",
              120,
              "#f0aac8",
              600,
              "#e97dae",
              2500,
              markerColor,
              9000,
              "#c30d68",
            ],
          ],
        ],
        "circle-radius": [
          "case",
          ["boolean", ["get", "selected"], false],
          9.5,
          ["boolean", ["get", "isSearchResultPin"], false],
          9.5,
          [
            "interpolate",
            ["linear"],
            ["to-number", ["coalesce", ["get", "numberOfVisitorReviews"], 0]],
            0,
            3.4,
            120,
            4.5,
            600,
            6,
            2500,
            8,
            9000,
            11,
          ],
        ],
        "circle-stroke-color": "#FFFFFF",
        "circle-stroke-width": [
          "case",
          ["boolean", ["get", "selected"], false],
          2.8,
          ["boolean", ["get", "isSearchResultPin"], false],
          2.8,
          [
            "interpolate",
            ["linear"],
            ["to-number", ["coalesce", ["get", "numberOfVisitorReviews"], 0]],
            0,
            1.1,
            300,
            1.7,
            1200,
            2.2,
          ],
        ],
      },
    },
    {
      id: HOTPLE_SELECTED_PLACE_LAYER_ID,
      type: "circle",
      source: PLACES_SOURCE_ID,
      filter: [
        "all",
        ["!", ["has", "point_count"]],
        [
          "any",
          ["==", ["get", "selected"], true],
          ["==", ["get", "isSearchResultPin"], true],
        ],
      ],
      paint: {
        "circle-color": "rgba(255, 77, 46, 0.08)",
        "circle-radius": 15,
        "circle-stroke-color": selectedMarkerColor,
        "circle-stroke-width": 2,
      },
    },
    {
      // 같은 자리에 여러 곳이 쌓였을 때만 개수를 얹는다.
      // (한 건물 안 가게들이 건물 중심 좌표 하나로 들어오는 경우)
      id: PLACE_STACK_COUNT_LAYER_ID,
      type: "symbol",
      source: PLACES_SOURCE_ID,
      filter: [
        "all",
        ["!", ["has", "point_count"]],
        [">", ["coalesce", ["get", "stackCount"], 1], 1],
      ],
      layout: {
        "text-field": ["to-string", ["get", "stackCount"]],
        "text-font": ["Noto Sans Medium"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 15, 9.5, 18, 11],
        "text-offset": [0.9, -0.9],
        "text-anchor": "left",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": "#ffffff",
        "text-halo-color": "rgba(20, 12, 16, 0.85)",
        "text-halo-width": 2.2,
      },
    },
    {
      id: PLACE_LABEL_LAYER_ID,
      type: "symbol",
      source: PLACES_SOURCE_ID,
      filter: ["!", ["has", "point_count"]],
      minzoom: 15,
      layout: {
        "text-field": ["get", "name"],
        "text-font": ["Noto Sans Medium"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 15, 10.5, 17, 11.5, 18, 12],
        "text-offset": [0, 1.25],
        "text-anchor": "top",
        "text-max-width": 9,
        // Slightly tighter collision padding (default 2) so a few more labels
        // survive in dense areas without becoming unreadable.
        "text-padding": 1,
        // IMPORTANCE-PRIORITIZED label collision (the Apple MapKit displayPriority
        // / Google Maps prominence approach). MapLibre places features in ASCENDING
        // sort-key order and, with text-allow-overlap:false (default), the
        // first-placed label wins the collision — so a LOWER sort key = higher
        // priority. We rank by prominence: the SELECTED / search pin always wins,
        // then by popularity (방문자 리뷰수) with rating (naver→average) as a
        // secondary nudge. Result: in a crowded area the popular places keep their
        // names and minor ones drop to dots-only (tap still opens them), instead of
        // label survival being arbitrary draw-order. numberOfVisitorReviews is the
        // same signal already driving pin size/color, so labels track pin weight.
        "symbol-sort-key": [
          "case",
          [
            "any",
            ["boolean", ["get", "selected"], false],
            ["boolean", ["get", "isSearchResultPin"], false],
          ],
          -1000000000,
          [
            "*",
            -1,
            [
              "+",
              ["to-number", ["coalesce", ["get", "numberOfVisitorReviews"], 0]],
              [
                "*",
                20,
                [
                  "to-number",
                  [
                    "coalesce",
                    ["get", "naverRating"],
                    ["get", "averageRating"],
                    0,
                  ],
                ],
              ],
            ],
          ],
        ],
      },
      paint: {
        "text-color": [
          "case",
          ["boolean", ["get", "selected"], false],
          HOTPLE_KOREA_COLORS.labelMain,
          HOTPLE_KOREA_COLORS.labelNormal,
        ],
        "text-halo-color": HOTPLE_KOREA_COLORS.labelHalo,
        "text-halo-width": 1.5,
        "text-halo-blur": 0.25,
      },
    },
  ],
});
